/**
 * Batch proportion schedule of an entry being swapped out.
 *
 * A material with batch proportion two feeds in two parts: proportion one in its normal batch order and proportion two
 * later, at its proportion-two order (setFeedFinish reorders the scale). While proportion one feeds, the entry's mft
 * holds only proportion one. When the material is swapped during proportion one, the substitute has to take over both
 * the rest of proportion one (now) and the whole proportion two (later), in the original material's slots.
 */
export type RemainingSchedule = {
  /** Quantity the substitute feeds now, in scale units. */
  now: number;
  /** Quantity left for proportion two, fed later at the proportion-two order; 0 when nothing is left for later. */
  later: number;
  total: number;
  /** Batch order slots of the swapped-out material, so the substitute feeds where it would have. */
  batchOrder?: number;
  proportionTwoOrder?: number;
};

/**
 * Whether batchMaterial feeds this material in batch proportions. Proportion targets on any other material are
 * ignored by batchMaterial, so a two-part schedule must not be handed to it.
 */
export const canBatchInProportions = (materialStore: any, itemId: unknown): boolean =>
  !!materialStore
  && (materialStore.isAggregate(itemId) || materialStore.isCement(itemId) || materialStore.isCementitious(itemId));

// Not checking isFeedFinish: changeMaterialMidBatch marks the swapped-out entry finished before reading its schedule,
// and a completed schedule always has isFeedFinishProportionOne set.
// Proportion one may be empty (batch proportion two at 100%, or a substitute created at the end of proportion one).
const isInProportionOne = (entry: any): boolean =>
  entry.proportionTwoTarget > 0
  && !entry.isFeedFinishProportionOne;

export const getBatchOrder = (entry: any): number | undefined =>
  entry.batchOrderOverride ?? entry.materialDetails?.batchOrder;

export const getProportionTwoOrder = (entry: any): number | undefined =>
  entry.proportionTwoOrderOverride ?? entry.materialDetails?.batchProportionTwoOrder;

/**
 * What is left of `scaleData`'s schedule once `currentWeight` of it has been dispensed.
 */
export const getRemainingSchedule = (scaleData: any, currentWeight: number): RemainingSchedule => {
  if (!isInProportionOne(scaleData)) {
    const now = Math.max(0, scaleData.mft - currentWeight);
    return { now, later: 0, total: now };
  }

  // Weights can overshoot their targets (up to ~10%). Like batchMaterial's (p1 + p2) - proportionOneActual, take the
  // remainder from what was actually dispensed, so an overrun of proportion one comes off proportion two.
  const total = Math.max(0, scaleData.proportionOneTarget + scaleData.proportionTwoTarget - currentWeight);
  const now = Math.max(0, scaleData.proportionOneTarget - currentWeight);
  const later = total - now;

  return {
    now,
    later,
    total,
    batchOrder: getBatchOrder(scaleData),
    proportionTwoOrder: getProportionTwoOrder(scaleData),
  };
};

/**
 * Quantity an entry still has scheduled, across both proportions.
 */
export const getScheduledRemaining = (entry: any): number => {
  if (entry.proportionTwoTarget > 0 && !entry.isFeedFinish) {
    return entry.isFeedFinishProportionOne
      ? entry.proportionOneTarget + entry.proportionTwoTarget - (entry.proportionOneActual || 0)
      : entry.proportionOneTarget + entry.proportionTwoTarget;
  }

  return entry.mft;
};
